import React, { useState } from 'react';
import './MiniCalendar.css';

/**
 * MiniCalendar - A compact date picker component for filtering.
 * Supports single and dual month layouts.
 * 
 * @param {Object} props
 * @param {string|Date} props.start - Selection start
 * @param {string|Date} props.end - Selection end
 * @param {Function} props.onSelect - (start, end) => void
 * @param {boolean} props.dualMonth - Whether to show two months side-by-side
 */
export default function MiniCalendar({ start, end, onSelect, dualMonth = false }) {
    const [viewDate, setViewDate] = useState(new Date(start || new Date()));
    const [hoverDate, setHoverDate] = useState(null);

    const isSameDay = (d1, d2) => {
        if (!d1 || !d2) return false;
        const a = new Date(d1); a.setHours(0,0,0,0);
        const b = new Date(d2); b.setHours(0,0,0,0);
        return a.getTime() === b.getTime();
    };

    const isInRange = (d) => {
        if (!start) return false;
        const s = new Date(start); s.setHours(0,0,0,0);
        const e = end ? new Date(end) : hoverDate;
        if (!e) return isSameDay(d, s);
        
        const eDate = new Date(e); eDate.setHours(0,0,0,0);
        const [min, max] = s < eDate ? [s, eDate] : [eDate, s];
        const current = new Date(d); current.setHours(0,0,0,0);
        return current >= min && current <= max;
    };

    const handleDateClick = (d) => {
        if (!start || (start && end)) {
            onSelect(d, null);
        } else {
            const s = new Date(start);
            if (d < s) onSelect(d, s);
            else onSelect(s, d);
        }
    };

    const handleNavigate = (offset) => {
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + offset, 1));
    };

    const renderMonth = (date, showPrev, showNext) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = (new Date(year, month, 1).getDay() + 6) % 7; // Mo=0
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        
        const days = [];
        for (let i = 0; i < firstDay; i++) days.push(null);
        for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));

        const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

        return (
            <div className="mc-month">
                <div className="mc-header">
                    <div style={{ width: 30 }}>
                        {showPrev && <button type="button" onClick={(e) => { e.stopPropagation(); handleNavigate(-1); }}>◀</button>}
                    </div>
                    <span>{MONTHS[month]} {year}</span>
                    <div style={{ width: 30 }}>
                        {showNext && <button type="button" onClick={(e) => { e.stopPropagation(); handleNavigate(1); }}>▶</button>}
                    </div>
                </div>
                <div className="mc-days-header">
                    {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(d => <div key={d}>{d}</div>)}
                </div>
                <div className="mc-grid">
                    {days.map((d, i) => {
                        if (!d) return <div key={`empty-${i}`} className="mc-cell empty" />;
                        const isStart = isSameDay(d, start);
                        const isEnd = isSameDay(d, end);
                        const inRange = isInRange(d);
                        return (
                            <div 
                                key={i} 
                                className={`mc-cell ${isStart ? 'is-start' : ''} ${isEnd ? 'is-end' : ''} ${inRange ? 'in-range' : ''}`}
                                onClick={(e) => { e.stopPropagation(); handleDateClick(d); }}
                                onMouseEnter={() => !end && setHoverDate(d)}
                                onMouseLeave={() => setHoverDate(null)}
                            >
                                {d.getDate()}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    if (dualMonth) {
        const secondMonthDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);
        return (
            <div className="mini-calendar-dual" onClick={e => e.stopPropagation()}>
                {renderMonth(viewDate, true, false)}
                {renderMonth(secondMonthDate, false, true)}
            </div>
        );
    }

    return (
        <div className="mini-calendar" onClick={e => e.stopPropagation()}>
            {renderMonth(viewDate, true, true)}
        </div>
    );
}
